import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';

interface OrphanedFile {
  name: string;
  path: string;
  size: number;
  created_at: string;
  bucket: string;
  url: string | null;
  isOrphaned: boolean;
  fileType: 'photo' | 'audio' | 'video' | 'other';
}

interface OrphanedStats {
  totalFiles: number;
  orphanedFiles: number;
  orphanedPercentage: number;
  totalSizeMB: number;
  orphanedSizeMB: number;
  orphanedSizePercentage: number;
}

type ScanPhase = 'fetching_profiles' | 'fetching_messages' | 'fetching_files' | 'analyzing' | 'complete';

interface ScanProgress {
  phase: ScanPhase;
  current: number;
  total: number;
  percentage: number;
  message: string;
}

interface BatchInfo {
  currentBatch: number;
  totalBatches: number;
  hasMore: boolean;
  itemsPerBatch: number;
}

interface CachedData {
  usedUrls: Set<string>;
  allFiles: any[];
  timestamp: number;
  expiresAt: number;
}

const BATCH_SIZE = 1000; // Processar 1000 arquivos por vez
const MAX_SELECTION = 50; // Máximo de arquivos para seleção
const DELETE_BATCH_SIZE = 10; // Deletar 10 arquivos por vez
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export const useOrphanedMediaDetector = () => {
  const [orphanedFiles, setOrphanedFiles] = useState<OrphanedFile[]>([]);
  const [stats, setStats] = useState<OrphanedStats>({
    totalFiles: 0,
    orphanedFiles: 0,
    orphanedPercentage: 0,
    totalSizeMB: 0,
    orphanedSizeMB: 0,
    orphanedSizePercentage: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [scanProgress, setScanProgress] = useState<ScanProgress>({
    phase: 'complete',
    current: 0,
    total: 0,
    percentage: 0,
    message: '',
  });
  const [deleteProgress, setDeleteProgress] = useState<ScanProgress>({
    phase: 'complete',
    current: 0,
    total: 0,
    percentage: 0,
    message: '',
  });
  const [batchInfo, setBatchInfo] = useState<BatchInfo>({
    currentBatch: 0,
    totalBatches: 0,
    hasMore: false,
    itemsPerBatch: BATCH_SIZE,
  });

  // Cache simples em memória
  const [cachedData, setCacheData] = useState<CachedData | null>(null);

  // Helper: atualizar progresso
  const updateProgress = (
    phase: ScanPhase,
    current: number,
    total: number,
    message: string
  ) => {
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
    setScanProgress({ phase, current, total, percentage, message });
  };

  // Helper: extrair nome do arquivo da URL
  const extractFileNameFromUrl = (url: string): string => {
    try {
      const parts = url.split('/');
      const fileName = parts[parts.length - 1];
      return fileName.split('?')[0]; // Remover query params
    } catch (error) {
      debugLogger.error('OrphanedMedia', 'Erro ao extrair nome do arquivo:', error);
      return '';
    }
  };

  // Helper: determinar tipo de arquivo
  const getFileType = (extension: string): 'photo' | 'audio' | 'video' | 'other' => {
    const photoExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif'];
    const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'opus'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'mpeg', 'mpg'];
    
    const ext = extension.toLowerCase();
    
    if (photoExtensions.includes(ext)) return 'photo';
    if (audioExtensions.includes(ext)) return 'audio';
    if (videoExtensions.includes(ext)) return 'video';
    return 'other';
  };

  // Helper: normalizar nome do arquivo
  const normalizeFileName = (fileName: string): string => {
    return fileName
      .replace(/-ultra-thumb\.(jpg|jpeg|png|webp)$/i, '.$1')
      .replace(/-medium-display\.(jpg|jpeg|png|webp)$/i, '.$1')
      .replace(/-full-quality\.(jpg|jpeg|png|webp)$/i, '.$1');
  };

  // Helper: verificar se arquivo está em uso
  const isFileUsed = (fileName: string, usedUrls: Set<string>): boolean => {
    const normalized = normalizeFileName(fileName);
    const baseName = fileName.replace(/\.[^.]+$/, '');
    
    return (
      usedUrls.has(fileName) ||
      usedUrls.has(normalized) ||
      Array.from(usedUrls).some(url => {
        const urlNormalized = normalizeFileName(url);
        const urlBaseName = url.replace(/\.[^.]+$/, '');
        return urlNormalized === normalized || urlBaseName === baseName;
      })
    );
  };

  // Buscar URLs em uso no banco de dados (com paginação)
  const fetchUsedUrls = async (): Promise<Set<string>> => {
    debugLogger.debug('OrphanedMedia', 'Buscando URLs em uso');
    
    const usedUrls = new Set<string>();

    try {
      // 1. Buscar profile_photos em uso com paginação
      updateProgress('fetching_profiles', 0, 100, 'Carregando perfis...');
      
      let profilePage = 0;
      let hasMoreProfiles = true;
      const PAGE_SIZE = 1000;

      while (hasMoreProfiles) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('main_photo_url, profile_photos')
          .range(profilePage * PAGE_SIZE, (profilePage + 1) * PAGE_SIZE - 1);

        if (profilesError) {
          debugLogger.error('OrphanedMedia', 'Erro ao buscar profiles:', profilesError);
          throw profilesError;
        }

        if (profiles && profiles.length > 0) {
          profiles.forEach((profile: any) => {
            if (profile.main_photo_url) {
              usedUrls.add(extractFileNameFromUrl(profile.main_photo_url));
            }
            if (Array.isArray(profile.profile_photos)) {
              profile.profile_photos.forEach((url: string) => {
                if (url) {
                  usedUrls.add(extractFileNameFromUrl(url));
                }
              });
            }
          });
          
          profilePage++;
          hasMoreProfiles = profiles.length === PAGE_SIZE;
          updateProgress('fetching_profiles', profilePage * PAGE_SIZE, profilePage * PAGE_SIZE + 100, `${usedUrls.size} URLs de perfis...`);
        } else {
          hasMoreProfiles = false;
        }
      }

      debugLogger.debug('OrphanedMedia', 'URLs de perfis:', usedUrls.size);

      // 2. Buscar chat_media em uso com paginação
      updateProgress('fetching_messages', 0, 100, 'Carregando mensagens...');
      
      let messagePage = 0;
      let hasMoreMessages = true;

      while (hasMoreMessages) {
        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select('media_url')
          .not('media_url', 'is', null)
          .range(messagePage * PAGE_SIZE, (messagePage + 1) * PAGE_SIZE - 1);

        if (messagesError) {
          debugLogger.error('OrphanedMedia', 'Erro ao buscar mensagens:', messagesError);
          throw messagesError;
        }

        if (messages && messages.length > 0) {
          messages.forEach((message: any) => {
            if (message.media_url) {
              usedUrls.add(extractFileNameFromUrl(message.media_url));
            }
          });
          
          messagePage++;
          hasMoreMessages = messages.length === PAGE_SIZE;
          updateProgress('fetching_messages', messagePage * PAGE_SIZE, messagePage * PAGE_SIZE + 100, `${usedUrls.size} URLs totais...`);
        } else {
          hasMoreMessages = false;
        }
      }

      debugLogger.debug('OrphanedMedia', 'Total de URLs em uso:', usedUrls.size);
      return usedUrls;
    } catch (error) {
      debugLogger.error('OrphanedMedia', 'Erro ao buscar URLs:', error);
      throw error;
    }
  };

  // Buscar arquivos de um bucket
  const fetchBucketFiles = async (bucketName: string): Promise<any[]> => {
    debugLogger.debug('OrphanedMedia', 'Buscando arquivos do bucket:', bucketName);
    
    const { data: files, error } = await supabase.storage
      .from(bucketName)
      .list('', { limit: 10000, sortBy: { column: 'created_at', order: 'desc' } });

    if (error) {
      debugLogger.error('OrphanedMedia', 'Erro ao buscar bucket:', bucketName, error);
      throw error;
    }

    debugLogger.debug('OrphanedMedia', 'Arquivos encontrados:', files?.length || 0, 'em', bucketName);
    return files || [];
  };

  // Processar arquivos e identificar órfãos
  const processFiles = (files: any[], usedUrls: Set<string>, bucketName: string): OrphanedFile[] => {
    const totalItems = files.length;
    
    const validFiles = files
      .filter(file => {
        // Remover items sem nome
        if (!file.name) return false;
        
        // Remover placeholders
        if (file.name.includes('.emptyFolderPlaceholder')) return false;
        
        // Remover diretórios (items sem ID são geralmente pastas)
        if (file.id === null || file.id === undefined) return false;
        
        // Remover items sem tamanho (podem ser diretórios vazios)
        if (!file.metadata?.size && file.metadata?.size !== 0) return false;
        
        // Verificar se tem extensão de arquivo válida
        const hasExtension = file.name.includes('.') && file.name.split('.').length > 1;
        if (!hasExtension) return false;
        
        return true;
      })
      .map(file => {
        const isOrphaned = !isFileUsed(file.name, usedUrls);
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        const fileType = getFileType(fileExtension);
        
        let fileUrl: string | null = null;
        if (bucketName === 'profile_photos') {
          const { data } = supabase.storage.from(bucketName).getPublicUrl(file.name);
          fileUrl = data.publicUrl;
        }

        return {
          name: file.name,
          path: file.name,
          size: file.metadata?.size || 0,
          created_at: file.created_at || '',
          bucket: bucketName,
          url: fileUrl,
          isOrphaned,
          fileType
        };
      });
    
    const filteredCount = totalItems - validFiles.length;
    if (filteredCount > 0) {
      debugLogger.debug('OrphanedMedia', `${bucketName}: ${totalItems} items → ${validFiles.length} arquivos (${filteredCount} diretórios/inválidos removidos)`);
    }
    
    return validFiles;
  };

  // Função principal de scan
  const scanOrphanedMedia = async (loadMore: boolean = false) => {
    setIsLoading(true);
    
    try {
      debugLogger.debug('OrphanedMedia', 'Iniciando scan');
      
      updateProgress('fetching_profiles', 0, 100, 'Preparando análise...');

      // Verificar cache (apenas se não for "carregar mais")
      let usedUrls: Set<string>;
      let allFiles: any[] = [];

      if (!loadMore && cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION) {
        debugLogger.debug('OrphanedMedia', 'Usando cache');
        usedUrls = cachedData.usedUrls;
        allFiles = cachedData.allFiles;
        updateProgress('analyzing', 0, allFiles.length, 'Usando cache...');
      } else {
        // Buscar dados novos
        usedUrls = await fetchUsedUrls();
        
        updateProgress('fetching_files', 0, 2, 'Carregando arquivos...');
        
        const [profileFiles, chatFiles] = await Promise.all([
          fetchBucketFiles('profile_photos'),
          fetchBucketFiles('chat_media'),
        ]);
        
        allFiles = [...profileFiles.map(f => ({ ...f, bucket: 'profile_photos' })), 
                     ...chatFiles.map(f => ({ ...f, bucket: 'chat_media' }))];
        
        updateProgress('fetching_files', 2, 2, `${allFiles.length} arquivos encontrados`);
        
        // Salvar em cache
        setCacheData({
          usedUrls,
          allFiles,
          timestamp: Date.now(),
          expiresAt: Date.now() + CACHE_DURATION,
        });
      }

      debugLogger.debug('OrphanedMedia', 'Total de arquivos:', allFiles.length);

      // Ordenar por data (mais recentes primeiro) e aplicar batch
      allFiles.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      const currentBatchNum = loadMore ? batchInfo.currentBatch + 1 : 1;
      const startIndex = (currentBatchNum - 1) * BATCH_SIZE;
      const endIndex = startIndex + BATCH_SIZE;
      const batchFiles = allFiles.slice(startIndex, endIndex);
      const hasMore = endIndex < allFiles.length;
      const totalBatches = Math.ceil(allFiles.length / BATCH_SIZE);

      updateProgress('analyzing', 0, batchFiles.length, 'Analisando arquivos...');

      // Processar arquivos do batch atual
      const profileBatch = batchFiles.filter(f => f.bucket === 'profile_photos');
      const chatBatch = batchFiles.filter(f => f.bucket === 'chat_media');
      
      const processedProfile = processFiles(profileBatch, usedUrls, 'profile_photos');
      const processedChat = processFiles(chatBatch, usedUrls, 'chat_media');
      const processedFiles = [...processedProfile, ...processedChat];

      // Atualizar progresso durante processamento
      updateProgress('analyzing', batchFiles.length, batchFiles.length, 'Concluindo análise...');

      // Mesclar com arquivos existentes se "carregar mais"
      const finalFiles = loadMore ? [...orphanedFiles, ...processedFiles] : processedFiles;
      setOrphanedFiles(finalFiles);

      // Calcular estatísticas com base em TODOS os arquivos (não apenas o batch)
      const allProcessedProfile = processFiles(allFiles.filter(f => f.bucket === 'profile_photos'), usedUrls, 'profile_photos');
      const allProcessedChat = processFiles(allFiles.filter(f => f.bucket === 'chat_media'), usedUrls, 'chat_media');
      const allProcessedFiles = [...allProcessedProfile, ...allProcessedChat];
      
      const allOrphaned = allProcessedFiles.filter(f => f.isOrphaned);
      const totalSize = allProcessedFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024);
      const orphanedSize = allOrphaned.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024);

      setStats({
        totalFiles: allProcessedFiles.length,
        orphanedFiles: allOrphaned.length,
        orphanedPercentage: allProcessedFiles.length > 0 
          ? (allOrphaned.length / allProcessedFiles.length) * 100 
          : 0,
        totalSizeMB: totalSize,
        orphanedSizeMB: orphanedSize,
        orphanedSizePercentage: totalSize > 0 
          ? (orphanedSize / totalSize) * 100 
          : 0,
      });

      setBatchInfo({
        currentBatch: currentBatchNum,
        totalBatches,
        hasMore,
        itemsPerBatch: BATCH_SIZE,
      });

      updateProgress('complete', batchFiles.length, batchFiles.length, 
        `Análise completa! ${allOrphaned.length} órfãos encontrados`);

      debugLogger.debug('OrphanedMedia', 'Scan completo! Batch:', currentBatchNum, 'de', totalBatches, '- Órfãos:', allOrphaned.length);

      toast.success(`Análise completa! ${allOrphaned.length} arquivos órfãos encontrados`);

    } catch (error) {
      debugLogger.error('OrphanedMedia', 'Erro durante scan:', error);
      toast.error('Erro ao analisar arquivos');
      updateProgress('complete', 0, 0, 'Erro na análise');
    } finally {
      setIsLoading(false);
    }
  };

  // Deletar arquivos selecionados
  const deleteOrphanedFiles = async (filesToDelete: OrphanedFile[]) => {
    if (filesToDelete.length === 0) {
      toast.error('Nenhum arquivo selecionado');
      return;
    }

    if (filesToDelete.length > MAX_SELECTION) {
      toast.error(`Selecione no máximo ${MAX_SELECTION} arquivos por vez`);
      return;
    }

    setIsDeleting(true);
    setDeleteProgress({
      phase: 'analyzing',
      current: 0,
      total: filesToDelete.length,
      percentage: 0,
      message: 'Preparando deleção...',
    });

    try {
      debugLogger.debug('OrphanedMedia', 'Iniciando deleção de', filesToDelete.length, 'arquivos');

      let successCount = 0;
      let errorCount = 0;

      // Agrupar por bucket
      const filesByBucket = filesToDelete.reduce((acc, file) => {
        if (!acc[file.bucket]) {
          acc[file.bucket] = [];
        }
        acc[file.bucket].push(file.path);
        return acc;
      }, {} as Record<string, string[]>);

      // Deletar em batches
      for (const [bucket, paths] of Object.entries(filesByBucket)) {
        debugLogger.debug('OrphanedMedia', 'Deletando', paths.length, 'arquivos de', bucket);
        
        for (let i = 0; i < paths.length; i += DELETE_BATCH_SIZE) {
          const batchPaths = paths.slice(i, i + DELETE_BATCH_SIZE);
          
          setDeleteProgress({
            phase: 'analyzing',
            current: successCount + errorCount,
            total: filesToDelete.length,
            percentage: Math.round(((successCount + errorCount) / filesToDelete.length) * 100),
            message: `Deletando ${successCount + errorCount + 1}/${filesToDelete.length}...`,
          });

          const { error } = await supabase.storage
            .from(bucket)
            .remove(batchPaths);

          if (error) {
            debugLogger.error('OrphanedMedia', 'Erro ao deletar batch de', bucket, ':', error);
            errorCount += batchPaths.length;
          } else {
            debugLogger.debug('OrphanedMedia', batchPaths.length, 'arquivos deletados de', bucket);
            successCount += batchPaths.length;
          }

          // Pequeno delay para evitar rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Limpar cache
      setCacheData(null);

      setDeleteProgress({
        phase: 'complete',
        current: filesToDelete.length,
        total: filesToDelete.length,
        percentage: 100,
        message: `${successCount} arquivo(s) deletado(s)`,
      });

      if (successCount > 0) {
        toast.success(`${successCount} arquivo(s) deletado(s) com sucesso`);
      }
      if (errorCount > 0) {
        toast.error(`Erro ao deletar ${errorCount} arquivo(s)`);
      }

      // Rescanear
      await scanOrphanedMedia();

    } catch (error) {
      debugLogger.error('OrphanedMedia', 'Erro durante deleção:', error);
      toast.error('Erro ao deletar arquivos');
      setDeleteProgress({
        phase: 'complete',
        current: 0,
        total: 0,
        percentage: 0,
        message: 'Erro na deleção',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    orphanedFiles,
    stats,
    isLoading,
    isDeleting,
    scanProgress,
    deleteProgress,
    batchInfo,
    scanOrphanedMedia,
    deleteOrphanedFiles,
    getFileType,
    MAX_SELECTION,
  };
};
